/** Per-strategy alert sounds: what plays now for Entry, Exit, Target and Alert, and a button that opens
 * the window to change them (docs/alert_sounds.md). The choice is kept in localStorage (browser
 * notifications are client-side, so no backend field is needed). Turn alerts on with the bell. */

import { useState, type ReactElement } from "react";
import { useServerSounds } from "@/hooks/useServerSounds";
import { getStrategySound, SOUND_EVENTS, type SoundMode } from "@/lib/soundPrefs";
import { soundName } from "@/lib/soundNames";
import { AlertSoundsDialog } from "./AlertSoundsDialog";

export function StrategyAlertSoundCard({
  strategyId,
  mode = "LIVE",
}: Readonly<{ strategyId: string; mode?: SoundMode }>): ReactElement {
  const [open, setOpen] = useState(false);
  const [, setVersion] = useState(0); // the sounds are read again after they are applied
  const files = useServerSounds().data ?? [];
  const drafts = files.filter((f) => f.status === "draft").length;
  return (
    <section className="card mb-3">
      <div className="mb-3 flex items-center gap-2">
        <i className="ti ti-music text-lg text-accent" />
        <h3>Alert sounds</h3>
        {drafts > 0 && (
          <span className="text-xs text-warning">{String(drafts)} new sound file</span>
        )}
        <button
          type="button"
          className="btn btn-secondary ml-auto"
          onClick={() => {
            setOpen(true);
          }}
        >
          <i className="ti ti-adjustments text-base" />
          Change sounds
        </button>
      </div>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
        {SOUND_EVENTS.map(({ id, label }) => (
          <div key={id}>
            <dt className="text-xs text-ink-muted">{label}</dt>
            <dd>{soundName(getStrategySound(strategyId, id, mode), files)}</dd>
          </div>
        ))}
      </dl>
      {open && (
        <AlertSoundsDialog
          strategyId={strategyId}
          mode={mode}
          onClose={() => {
            setOpen(false);
          }}
          onApplied={() => {
            setVersion((n) => n + 1);
          }}
        />
      )}
    </section>
  );
}
